// What a load can move by, and the D9 arithmetic, mirroring the engine
// (backend/training/engine/loads.py `_rack`, `load_step`, `round_load`;
// engine/history.py `epley`, `load_for`) so the logging sheet's steppers
// and suggestions land on the same numbers the plan does.

// Pounds per kilogram (exact by definition of the pound).
export const LB_PER_KG = 2.20462262185;
// Added weight for bodyweight moves: 5 lb, or its kg twin on a real plate.
export const ADDED_STEP = { lb: 5, kg: 2.5 };
// Epley: e1RM = load × (1 + reps-to-failure / 30) (engine_constants EPLEY_DIVISOR).
export const EPLEY_DIVISOR = 30;
// Beyond this many reps to failure Epley stops meaning much (E1RM_MAX_REPS).
export const E1RM_MAX_REPS = 12;
// A set logged without reps in reserve compares as 2 (UNKNOWN_RIR).
export const UNKNOWN_RIR = 2;
// "5 or more" in reserve is only a floor: the next find-your-weight load is
// 20% heavier than Epley says (FIND_JUMP, spec §5.8).
export const RIR_MAX = 5;
export const FIND_JUMP = 0.2;

const EPS = 1e-9; // float slack: "exactly on a step", "exactly a tie"
const RACK_STEPS = { barbell: "barbell_step", dumbbells: "dumbbell_step", cable: "cable_step" };

const tidy = (value) => Math.round(value * 10000) / 10000;

/** `value` in `from` units, in `to` units (a missing unit is taken as `to`). */
export function convert(value, from, to) {
  if (!from || from === to) return value;
  return to === "lb" ? value * LB_PER_KG : value / LB_PER_KG;
}

/**
 * What an exercise's load moves by: "barbell", "dumbbells", "cable",
 * "other" (an external custom move needing none of those), "bodyweight"
 * or "band".
 */
export function rackOf(exercise) {
  const kind = exercise?.load_kind ?? "external";
  if (kind !== "external") return kind;
  const requires = exercise?.requires ?? [];
  for (const item of ["barbell", "dumbbells", "cable"]) {
    if (requires.includes(item)) return item;
  }
  return "other";
}

/**
 * The smallest change this exercise's load can make, in `units` (default:
 * the settings'). Equipment steps are stored in the equipment block's own
 * units and converted. A band move has no load step (0); a custom move on
 * none of the known racks moves by the finest step the gym has.
 */
export function loadStep(exercise, settings, units = settings?.units ?? "lb") {
  const rack = rackOf(exercise);
  if (rack === "band") return 0;
  if (rack === "bodyweight") return ADDED_STEP[units] ?? ADDED_STEP.lb;
  const equipment = settings?.equipment ?? {};
  const gear = (value) => tidy(convert(Number(value) || 0, equipment.units, units));
  if (RACK_STEPS[rack]) return gear(equipment[RACK_STEPS[rack]]);
  const positive = Object.values(RACK_STEPS)
    .map((field) => gear(equipment[field]))
    .filter((step) => step > 0);
  return positive.length ? Math.min(...positive) : 0;
}

/**
 * The heaviest dumbbell (`equipment.dumbbell_max`) in `units`, for a
 * dumbbell move — null for any other move, or when the settings don't say.
 */
export function dumbbellMax(exercise, settings, units = settings?.units ?? "lb") {
  if (rackOf(exercise) !== "dumbbells") return null;
  const equipment = settings?.equipment ?? {};
  const max = tidy(convert(Number(equipment.dumbbell_max) || 0, equipment.units, units));
  return max > 0 ? max : null;
}

/** Dumbbell moves stop at the heaviest dumbbell (`equipment.dumbbell_max`). */
export function clampDumbbell(load, exercise, settings, units = settings?.units ?? "lb") {
  const max = dumbbellMax(exercise, settings, units);
  return max === null ? load : Math.min(load, max);
}

/** The nearest multiple of `step`, ties down (no step: unchanged). */
export function roundToStep(load, step) {
  if (!(step > 0)) return load;
  let n = Math.floor(load / step + EPS);
  if (load / step - n > 0.5 + EPS) n += 1;
  return tidy(n * step);
}

/** Epley's estimated one-rep max from a set. */
export function epley(load, repsToFailure) {
  return load * (1 + repsToFailure / EPLEY_DIVISOR);
}

/** The load that many reps to failure is, from an estimated max. */
export function loadFor(e1rm, repsToFailure) {
  return e1rm / (1 + repsToFailure / EPLEY_DIVISOR);
}

/**
 * D9, within a session: the next set's load from the last set — Epley on
 * its reps + reps in reserve, re-expressed at the target reps + reserve,
 * rounded to the step (ties down). A set at "5+" in reserve says only that
 * it was light, so the next one is 20% heavier on top (spec §5.8).
 *
 * `offset` is the part of the effective load that isn't on the bar or the
 * belt — for a bodyweight move, bodyweight − the band's assist — so the
 * estimate runs on the real load while `load` stays the added weight.
 *
 * `target` is the effective load it aims at, before rounding — what a
 * bodyweight move's setup is picked from (setupFor).
 *
 * @returns {{load: number, reps: number, rir: number, e1rm: number, target: number} | null}
 *   null when the last set says nothing (no reps, no load to go on).
 */
export function nextSetSuggestion(lastSet, targetReps, targetRir, step, { offset = 0 } = {}) {
  const reps = Number(lastSet?.reps);
  const load = Number(lastSet?.load ?? 0);
  if (!lastSet || !(reps > 0) || !Number.isFinite(load)) return null;
  const effective = load + offset;
  if (!(effective > 0)) return null;
  const rir = lastSet.rir === null || lastSet.rir === undefined ? UNKNOWN_RIR : Number(lastSet.rir);
  const e1rm = epley(effective, Math.min(reps + rir, E1RM_MAX_REPS));
  const jump = rir >= RIR_MAX ? 1 + FIND_JUMP : 1;
  const target = loadFor(e1rm, targetReps + targetRir) * jump;
  return {
    load: Math.max(0, roundToStep(target - offset, step)),
    reps: targetReps,
    rir: targetRir,
    e1rm: tidy(e1rm),
    target: tidy(target),
  };
}

/**
 * A bodyweight move's setup for an effective load (engine/loads.py
 * `_translate`, without its floor and ceiling): each band (bodyweight less
 * its help, when that leaves something to lift), bodyweight, and added
 * weight on the step — the nearest to `target`, ties to the lighter.
 * `bands` are `{name, assist}` in the same units as the loads.
 *
 * @returns {{load: number, band: string, effective: number}} `load` is
 *   the added weight (0 on a band or bodyweight alone).
 */
export function setupFor(target, bodyweight, bands, step) {
  const options = (bands ?? [])
    .filter((band) => bodyweight - Number(band.assist) > 0)
    .map((band) => ({ effective: tidy(bodyweight - Number(band.assist)), load: 0, band: band.name }));
  options.push({ effective: bodyweight, load: 0, band: "" });
  if (step > 0) {
    for (let k = 1; k * step <= Math.max(target - bodyweight, 0) + 4 * step; k += 1) {
      options.push({ effective: tidy(bodyweight + k * step), load: tidy(k * step), band: "" });
    }
  }
  let pick = options[0];
  for (const option of options) {
    const gap = Math.abs(option.effective - target);
    const best = Math.abs(pick.effective - target);
    if (gap < best - EPS || (Math.abs(gap - best) <= EPS && option.effective < pick.effective)) pick = option;
  }
  return pick;
}
